import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { when } from "@/lib/rides";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LABEL = {
  rider: { rider_admin: "Scoopz Admin", rider_driver: "Your driver" },
  driver: { rider_driver: "Rider", driver_admin: "Scoopz Admin" },
  admin: { rider_admin: "Rider", driver_admin: "Driver", rider_driver: "Rider ↔ Driver" },
  adminDriver: { rider_admin: "Rider (as Scoopz Admin)", rider_driver: "Rider (as driver)" },
};
const initials = (n) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

function Thread({ rideId, onClose }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["messages", rideId], queryFn: () => api.get(`/rides/${rideId}/messages`).then((r) => r.data), refetchInterval: 5000 });
  const [channel, setChannel] = useState(null);
  const [text, setText] = useState("");
  const end = useRef(null);
  const ch = channel || data?.channels?.[0];
  const msgs = data?.messages?.filter((m) => m.channel === ch) || [];
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs.length, ch]);
  useEffect(() => {
    if (data?.messages?.length) api.post(`/rides/${rideId}/messages/read`).then(() => qc.invalidateQueries({ queryKey: ["notifications"] })).catch(() => {});
  }, [data?.messages?.length, rideId]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rides/${rideId}/messages`, { channel: ch, text });
      setText("");
      qc.invalidateQueries({ queryKey: ["messages", rideId] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const readOnly = data?.role === "admin" && ch === "rider_driver";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-md" data-testid="messages-dialog">
        <DialogHeader><DialogTitle className="font-display">Messages</DialogTitle></DialogHeader>
        <div className="flex gap-1.5">
          {data?.channels?.map((c) => (
            <button key={c} type="button" onClick={() => setChannel(c)} data-testid={`messages-channel-${c}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${c === ch ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-muted-foreground"}`}>
              {(data.roles?.includes("admin") && data.role === "driver" ? LABEL.adminDriver[c] : LABEL[data.role][c]) || LABEL.admin[c]}{data.unread[c] ? ` · ${data.unread[c]}` : ""}
            </button>
          ))}
        </div>
        {data && data.role === "rider" && !data.channels.includes("rider_driver") && <p className="text-xs text-muted-foreground">Driver chat opens once a driver is assigned.</p>}
        <div className="min-h-[200px] flex-1 space-y-2 overflow-y-auto py-2" data-testid="messages-list">
          {!msgs.length && <p className="text-sm text-muted-foreground">No messages yet.</p>}
          {msgs.map((m) => (
            <div key={m.id} className={`flex gap-2 ${m.mine ? "flex-row-reverse" : ""}`} data-testid="message-item">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-muted text-[11px] font-bold">{initials(m.sender_name)}</span>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                <p className="text-[10px] font-semibold opacity-70">{m.sender_name}</p>
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className="mt-0.5 text-[10px] opacity-60">{when(m.created_at)}{m.mine ? (m.read ? " · Read" : " · Sent") : ""}</p>
              </div>
            </div>
          ))}
          <div ref={end} />
        </div>
        {!readOnly && ch && (
          <form onSubmit={send} className="flex gap-2">
            <Input value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} placeholder="Type a message" className="rounded-full" data-testid="message-input" />
            <Button type="submit" size="icon" className="rounded-full" disabled={!text.trim()} data-testid="message-send-button"><Send className="h-4 w-4" /></Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function MessagesButton({ rideId, className = "", label = "Messages" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" className={`rounded-full ${className}`} onClick={() => setOpen(true)} data-testid={`messages-button-${rideId}`}>
        <MessageCircle className="mr-1.5 h-4 w-4" />{label}
      </Button>
      {open && <Thread rideId={rideId} onClose={() => setOpen(false)} />}
    </>
  );
}
